import PerspectiveCard from '@/components/perspectives/PerspectiveCard';
import { type DragSourceMonitor, useDrag } from 'react-dnd';
import DragType from './DragType.enum';
import type PerspectiveCategory from '@/lib/constants/PerspectiveCategory.enum';
import useApi from '@/lib/api/useApi';
import useIsPortfolioReadonly from '@/components/ChallengePortfolio/NormalView/useIsPortfolioReadonly';
import { type PerspectiveWithSublevelsInfo } from '@/lib/types';
import {
  type PerspectiveDragItem,
  type PerspectiveDropResult,
} from './DragDropTypes';
import React from 'react';

type DraggablePerspectiveProps = {
  perspective: PerspectiveWithSublevelsInfo;
  category: PerspectiveCategory;
  onUpdateCategory: (category: PerspectiveCategory) => void;
  onOpen?: () => Promise<void>;
};

const DraggablePerspective = ({
  perspective,
  category,
  onUpdateCategory,
  onOpen,
}: DraggablePerspectiveProps) => {
  const api = useApi();
  const portfolioIsReadonly = useIsPortfolioReadonly();

  const [, dragRef] = useDrag({
    type: DragType.PERSPECTIVE,
    item: {
      perspective,
      category,
    } satisfies PerspectiveDragItem,
    end: (
      _,
      monitor: DragSourceMonitor<PerspectiveDragItem, PerspectiveDropResult>,
    ) => {
      const dropResult = monitor.getDropResult();

      if (dropResult === null) {
        return;
      }

      const newCategory = dropResult.category;

      if (category === newCategory) {
        return;
      }

      api.post(`/perspectives/${perspective.id}/category`, {
        category: newCategory,
      });

      onUpdateCategory(newCategory);
    },
  });

  return (
    <PerspectiveCard
      perspective={perspective}
      onOpen={onOpen}
      draggable={!portfolioIsReadonly}
      ref={
        portfolioIsReadonly
          ? null
          : (dragRef as unknown as React.Ref<HTMLLIElement>)
      }
    />
  );
};

export default DraggablePerspective;
